import { useQuery } from '@tanstack/react-query';
import { showToast, TEN_SECONDS } from 'lib/utils';
import { makeRemoteGetQueueAttachment } from 'main/factories/usecases/queue/remote-get-queue-attachment';
import { transformFileType } from 'presentation/components/Queue/utils/fileUtils';

export const useGetQueueAttachmentQuery = (attachmentId: string, fileType: string) => {
  return useQuery({
    queryKey: ['queueAttachment', attachmentId],
    queryFn: async () => {
      try {
        const service = makeRemoteGetQueueAttachment(attachmentId);
        const response = await service.get();

        // Try to handle various response formats
        if (typeof response === 'string') {
          // If it's base64 data URL
          if ((response as string).startsWith('data:')) {
            return fetch(response).then(res => res.blob());
          }

          // Check if it's a base64 string without the prefix
          if (/^[A-Za-z0-9+/=]+$/.test(response)) {
            try {
              const binary = atob(response);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
              }
              return new Blob([bytes], { type: transformFileType(fileType) });
            } catch {
              showToast('Error fetching image from QBO. Please try again later.', 'error', TEN_SECONDS);
            }
          }

          // Try as raw binary string
          return new Blob([response], { type: transformFileType(fileType) });
        }

        // If it's already a blob or other binary type
        return new Blob([response as BlobPart], { type: transformFileType(fileType) });
      } catch (error) {
        showToast('Error fetching image from QBO. Please try again later.', 'error', TEN_SECONDS);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!attachmentId,
  });
};
